// src/admin/pages/AdminAnalytics.jsx
import { useState, useEffect } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Simple bar chart component
const BarChart = ({ data, color = "#4318FF", maxVal, height = 120 }) => {
  const max = maxVal || Math.max(...data, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height, paddingTop: 8 }}>
      {data.map((val, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <div style={{ fontSize: 9, color: "#a3aed0", fontWeight: 600 }}>{val > 0 ? val : ""}</div>
          <div style={{
            width: "100%",
            height: max > 0 ? `${Math.max((val / max) * (height - 20), val > 0 ? 4 : 0)}px` : "0px",
            background: val > 0 ? color : "#f4f7fe",
            borderRadius: "4px 4px 0 0",
            transition: "height 0.5s ease",
            minHeight: val > 0 ? 4 : 0,
          }} />
          <div style={{ fontSize: 9, color: "#a3aed0" }}>{MONTHS[i]}</div>
        </div>
      ))}
    </div>
  );
};

// Dual bar chart (this year vs last year)
const DualBarChart = ({ thisYear, lastYear, height = 120 }) => {
  const max = Math.max(...thisYear, ...lastYear, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height, paddingTop: 8 }}>
      {thisYear.map((val, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <div style={{ width: "100%", display: "flex", gap: 1, alignItems: "flex-end" }}>
            <div style={{
              flex: 1,
              height: max > 0 ? `${Math.max((lastYear[i] / max) * (height - 24), lastYear[i] > 0 ? 3 : 0)}px` : "0px",
              background: "#e0e5f2", borderRadius: "3px 3px 0 0",
            }} />
            <div style={{
              flex: 1,
              height: max > 0 ? `${Math.max((val / max) * (height - 24), val > 0 ? 3 : 0)}px` : "0px",
              background: "#4318FF", borderRadius: "3px 3px 0 0",
            }} />
          </div>
          <div style={{ fontSize: 9, color: "#a3aed0" }}>{MONTHS[i]}</div>
        </div>
      ))}
    </div>
  );
};

const AdminAnalytics = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("owners"); // owners | reports | revenue

  const load = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/analytics`, { headers: adminAuthHeader() });
      const json = await res.json();
      setData(json);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <AdminLayout>
      <div style={{ textAlign: "center", padding: 60, color: "#a3aed0" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
        Loading analytics...
      </div>
    </AdminLayout>
  );

  if (!data) return (
    <AdminLayout>
      <div style={{ textAlign: "center", padding: 60, color: "#dc2626" }}>Failed to load analytics</div>
    </AdminLayout>
  );

  const { summary, charts, topCities, year, lastYear } = data;

  const STAT_CARDS = [
    { label: "Total Owners",    value: summary.totalOwners,    icon: "👥", color: "#4318FF", bg: "#ede9fe" },
    { label: "Active Owners",   value: summary.activeOwners,   icon: "✅", color: "#16a34a", bg: "#dcfce7" },
    { label: "New This Month",  value: summary.newThisMonth,   icon: "🆕", color: "#0891b2", bg: "#ecfeff" },
    { label: "Pending Payment", value: summary.pendingOwners,  icon: "⏳", color: "#d97706", bg: "#fef3c7" },
    { label: "Total Reports",   value: summary.totalReports,   icon: "🚨", color: "#dc2626", bg: "#fee2e2" },
    { label: "Total Revenue",   value: `₹${summary.totalRevenue}`, icon: "💰", color: "#16a34a", bg: "#dcfce7" },
    { label: "Searches/Month",  value: summary.searchesThisMonth, icon: "🔍", color: "#7c3aed", bg: "#ede9fe" },
    { label: "YoY Growth",      value: `${summary.growthRate > 0 ? "+" : ""}${summary.growthRate}%`, icon: "📈", color: summary.growthRate >= 0 ? "#16a34a" : "#dc2626", bg: summary.growthRate >= 0 ? "#dcfce7" : "#fee2e2" },
  ];

  return (
    <AdminLayout>
      <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p className="admin-welcome">System Overview</p>
          <h2 className="admin-title">📊 Analytics & Usage Stats</h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#a3aed0" }}>Showing data for</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: "#4318FF" }}>{year}</span>
          <button onClick={load}
            style={{ padding: "8px 14px", background: "#f4f7fe", border: "1.5px solid #e0e5f2", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#4318FF" }}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* ── Summary Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
        {STAT_CARDS.map(c => (
          <div key={c.label} style={{ background: c.bg, borderRadius: 14, padding: "14px 16px", border: `1.5px solid ${c.color}20` }}>
            <div style={{ fontSize: 22 }}>{c.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: c.color, marginTop: 4 }}>{c.value}</div>
            <div style={{ fontSize: 11, color: c.color, fontWeight: 600, opacity: 0.8 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── Owner Status Breakdown ── */}
      <div style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#1b2559" }}>👥 Owner Status Breakdown</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "Active",   value: summary.activeOwners,  color: "#16a34a", bg: "#dcfce7" },
            { label: "Trial",    value: summary.trialOwners,   color: "#0891b2", bg: "#ecfeff" },
            { label: "Pending",  value: summary.pendingOwners, color: "#d97706", bg: "#fef3c7" },
            { label: "Expired",  value: summary.expiredOwners, color: "#6b7280", bg: "#f3f4f6" },
            { label: "Blocked",  value: summary.blockedOwners, color: "#dc2626", bg: "#fee2e2" },
          ].map(s => {
            const pct = summary.totalOwners > 0 ? Math.round((s.value / summary.totalOwners) * 100) : 0;
            return (
              <div key={s.label} style={{ flex: 1, minWidth: 100, background: s.bg, borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontSize: 10, color: s.color, opacity: 0.7 }}>{pct}%</div>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 14, height: 8, borderRadius: 99, background: "#f4f7fe", overflow: "hidden", display: "flex" }}>
          {[
            { value: summary.activeOwners,  color: "#16a34a" },
            { value: summary.trialOwners,   color: "#0891b2" },
            { value: summary.pendingOwners, color: "#d97706" },
            { value: summary.expiredOwners, color: "#6b7280" },
            { value: summary.blockedOwners, color: "#dc2626" },
          ].map((s, i) => (
            <div key={i} style={{
              width: summary.totalOwners > 0 ? `${(s.value / summary.totalOwners) * 100}%` : "0%",
              background: s.color, transition: "width 0.5s",
            }} />
          ))}
        </div>
      </div>

      {/* ── Chart Tabs ── */}
      <div style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "2px solid #e0e5f2" }}>
          {[
            { key: "owners",  label: "👥 Owner Growth" },
            { key: "reports", label: "🚨 Fraud Reports" },
            { key: "revenue", label: "💰 Revenue" },
            { key: "active",  label: "🟢 Active Users" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: "8px 16px", border: "none", cursor: "pointer", background: "none",
                fontSize: 13, fontWeight: 700,
                color: tab === t.key ? "#4318FF" : "#a3aed0",
                borderBottom: tab === t.key ? "3px solid #4318FF" : "3px solid transparent",
                marginBottom: "-2px",
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Owner Growth Chart */}
        {tab === "owners" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559" }}>Owner Registrations — {year} vs {lastYear}</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                  {year}: <b style={{ color: "#4318FF" }}>{summary.totalThisYear}</b> new owners &nbsp;|&nbsp;
                  {lastYear}: <b style={{ color: "#a3aed0" }}>{summary.totalLastYear}</b> new owners &nbsp;|&nbsp;
                  Growth: <b style={{ color: summary.growthRate >= 0 ? "#16a34a" : "#dc2626" }}>{summary.growthRate > 0 ? "+" : ""}{summary.growthRate}%</b>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: "#4318FF" }} /> {year}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: "#e0e5f2" }} /> {lastYear}
                </span>
              </div>
            </div>
            <DualBarChart thisYear={charts.registrationsThisYear} lastYear={charts.registrationsLastYear} height={140} />
          </>
        )}

        {/* Fraud Reports Chart */}
        {tab === "reports" && (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559" }}>Fraud Reports Submitted — {year}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                Total: <b style={{ color: "#dc2626" }}>{summary.totalReports}</b> &nbsp;|&nbsp;
                Approved: <b style={{ color: "#16a34a" }}>{summary.approvedReports}</b> &nbsp;|&nbsp;
                Pending: <b style={{ color: "#d97706" }}>{summary.pendingReports}</b>
              </div>
            </div>
            <BarChart data={charts.reportsPerMonth} color="#dc2626" height={140} />
          </>
        )}

        {/* Revenue Chart */}
        {tab === "revenue" && (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559" }}>Subscription Revenue — {year}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                Total collected: <b style={{ color: "#16a34a" }}>₹{summary.totalRevenue}</b>
              </div>
            </div>
            <BarChart data={charts.revenuePerMonth} color="#16a34a" height={140} />
            {/* Revenue table */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, marginTop: 12 }}>
              {MONTHS.map((m, i) => (
                <div key={m} style={{ background: "#f9faff", borderRadius: 8, padding: "6px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: "#a3aed0" }}>{m}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: charts.revenuePerMonth[i] > 0 ? "#16a34a" : "#a3aed0" }}>
                    {charts.revenuePerMonth[i] > 0 ? `₹${charts.revenuePerMonth[i]}` : "—"}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Active Users Chart */}
        {tab === "active" && (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559" }}>Unique Active Owners per Month — {year}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                Owners who logged in at least once each month
              </div>
            </div>
            <BarChart data={charts.activeOwnersPerMonth} color="#0891b2" height={140} />
          </>
        )}
      </div>

      {/* ── Bottom row: Top Cities + Fraud breakdown ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Top Cities */}
        <div style={{ background: "#fff", borderRadius: 16, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#1b2559" }}>📍 Top Owner Cities</h3>
          {topCities.length === 0 ? (
            <p style={{ color: "#a3aed0", fontSize: 13 }}>No data yet</p>
          ) : (
            topCities.map((c, i) => {
              const pct = summary.totalOwners > 0 ? Math.round((c.count / summary.totalOwners) * 100) : 0;
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1b2559" }}>{c.city || "Unknown"}</span>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{c.count} owners ({pct}%)</span>
                  </div>
                  <div style={{ height: 6, background: "#f4f7fe", borderRadius: 99 }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#4318FF", borderRadius: 99, transition: "width 0.5s" }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Fraud Report Status */}
        <div style={{ background: "#fff", borderRadius: 16, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#1b2559" }}>🚨 Fraud Report Status</h3>
          {[
            { label: "Approved",     value: summary.approvedReports, color: "#16a34a" },
            { label: "Pending",      value: summary.pendingReports,  color: "#d97706" },
            { label: "Rejected",     value: summary.rejectedReports, color: "#dc2626" },
          ].map(s => {
            const pct = summary.totalReports > 0 ? Math.round((s.value / summary.totalReports) * 100) : 0;
            return (
              <div key={s.label} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#1b2559" }}>{s.label}</span>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{s.value} ({pct}%)</span>
                </div>
                <div style={{ height: 6, background: "#f4f7fe", borderRadius: 99 }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: s.color, borderRadius: 99, transition: "width 0.5s" }} />
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 16, padding: "12px 14px", background: "#f4f7fe", borderRadius: 10, textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#4318FF" }}>{summary.totalReports}</div>
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Total Reports All Time</div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
